import React from 'react';

import Link from '@/components/switchback/Link/Link';
import { IBlock, ImageProps } from '@/components/templates/AppsTemplate';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { UserType } from '@/services/appsSMS';

import RichText from '../wagtail/RichText/RichText';

interface IAppInfoSplit {
  left: IBlock<string>[];
  right: IBlock<ImageProps>[];
}

interface IListItemsProps {
  date: string;
  image: ImageProps;
  description: string;
  title: string;
  url: string;
}

interface IAppInfoProps {
  split: IAppInfoSplit;
  list: IListItemsProps[];
  appType: UserType;
}

export const getDownloadLink = (idx: number, appType: UserType) => {
  if (idx === 0) {
    if (appType === UserType.renter) {
      return '/apps/guest/ios';
    } else {
      return '/apps/host/ios';
    }
  } else {
    if (appType === UserType.renter) {
      return '/apps/guest/android';
    } else {
      return '/apps/host/android';
    }
  }
};

const AppInfo: React.FC<IAppInfoProps> = ({ split, list = [], appType }) => {
  const richtext = split?.left?.find(block => block.type === 'richtext');
  const image = split?.right?.find(block => block.type === 'image');
  let downloadIcons: ImageProps[] = [];
  const features: IListItemsProps[] = [];

  list.forEach(listItem => {
    if (features.length < 3) {
      features.push(listItem);
    } else {
      downloadIcons.push({ url: listItem.url, title: listItem.title });
    }
  });

  if (downloadIcons.length > 2) {
    downloadIcons = downloadIcons.slice(-2);
  }

  const isRenter = appType === 'renter';
  return (
    <div className={!isRenter ? 'bg-canvas-100' : ''}>
      <div
        className={`flex gap-x-20 ${
          !isRenter ? 'flex-row-reverse' : ''
        } md:max-w-sm lg:max-w-xl mx-auto px-4 sm:px-8 lg:px-12`}>
        {image && (
          <ResponsiveImage
            src={image.value.url}
            alt={image.value.title}
            className="hidden object-cover w-84 lg:block"
          />
        )}

        <div className="flex-auto my-16">
          {richtext && (
            <RichText
              content={richtext.value}
              className="[&>h3]:!mb-3"
              tag="div"
              containerClassName="mb-8"
            />
          )}

          {features.map(({ title, description }, idx) => (
            <div key={`${appType}-app-feature-${idx}`} className="flex items-start mb-8">
              <div>
                <h5 className="text-gray-800 h5">{title}</h5>
                <p className="text-gray-700 body-base">{description}</p>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap">
            {downloadIcons.map((icon, idx) => (
              <Link
                data-testid={`${appType}-app-download-link-${idx}`}
                key={`${appType}-app-download-link-${idx}`}
                href={getDownloadLink(idx, appType)}
                aria-label={icon.title}
                target="_blank"
                rel="noopener"
                className="mr-6">
                <ResponsiveImage src={icon.url} alt={icon.title} className="w-auto h-12" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

AppInfo.displayName = 'AppInfo';
export default AppInfo;
